/**
 * Map Details (in-game) — preview a game's map structure: region groupings,
 * continent bonuses, territory names, and adjacencies.
 *
 * Thin wrapper: resolves the map (live game map if available, else the fetched
 * MapDef for the lobby/pre-start flow) and renders the shared MapExplorerView
 * (same component the standalone gallery explorer uses — see
 * components/map/MapExplorerView).
 */

import React from 'react'
import { View, Text, TouchableOpacity, ActivityIndicator } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { MapExplorerView } from '../../../../components/map/MapExplorerView'
import { useMap } from '../../../../hooks/useMaps'
import { useGameWithMap } from '../../../../hooks/useGameWithMap'
import { goBackOrHome } from '../../../../lib/goBack'
import type { MapDef } from '@warcarp/api-types'
import { initialRegionId } from '../../../../lib/mapDetailsDeepLink'

export default function MapDetailsScreen() {
  const { id, mapId, region } = useLocalSearchParams<{ id: string; mapId?: string; region?: string }>()
  const router = useRouter()
  // hydratedState.map carries the live game's map (with current territory ownership).
  // For the lobby / pre-start flow, mapDef is fetched separately via useMap.
  const { hydratedState, mapDef: gameMapDef } = useGameWithMap(id)
  const gameMap = hydratedState?.map ?? gameMapDef ?? null

  // Prefer the live game map if available; otherwise hit /api/v1/maps/:id
  // with the query-param mapId (lobby flow where game has no active state).
  const fetchId = !gameMap && mapId ? mapId : undefined
  const { data: fetchedMap, isLoading, error } = useMap(fetchId)
  const map: MapDef | null = gameMap ?? fetchedMap ?? null

  if (isLoading) {
    return (
      <SafeAreaView className="flex-1 bg-canvas items-center justify-center">
        <ActivityIndicator color="#8aa14a" />
        <Text className="font-typed text-ink-soft mt-2">Loading map…</Text>
      </SafeAreaView>
    )
  }

  if (error || !map) {
    return (
      <SafeAreaView className="flex-1 bg-canvas items-center justify-center gap-3 px-6">
        <Text className="font-stencil text-ink uppercase">Map not found</Text>
        <TouchableOpacity
          onPress={() => goBackOrHome(router)}
          accessibilityLabel="Go back"
          accessibilityRole="button"
          className="bg-paper-up border border-olive rounded-md px-4 py-2"
        >
          <View>
            <Text className="font-stencil text-ink">← Back</Text>
          </View>
        </TouchableOpacity>
      </SafeAreaView>
    )
  }

  return (
    <MapExplorerView map={map} onBack={() => goBackOrHome(router)} initialRegionId={initialRegionId(region)} />
  )
}
